import { useEffect, useState } from "react";
import { Link, Route, Switch } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Message, Breadcrumb } from "semantic-ui-react";
import PageWrap from "components/PageWrap/PageWrap";
import LoadResolver from "components/LoadResolver";
import AssetTable from "components/AssetTable/AssetTable";
import TransitionBetween from "components/TransitionBetween/TransitionBetween";
import CampaignDisplay from "components/CampaignDisplay/CampaignDisplay";
import {
  getGroupedAssets,
  getCampaignLookup,
  getAgencys,
  getUsers,
  getAssets,
  getCampaigns,
} from "store/selectors";
import DeliveriesTable from "components/DeliveriesTable/DeliveriesTable";
import { useTranslation } from "react-i18next";

function DeliveriesPage(props) {
  const { match } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const activeUser = useSelector((state) => state.activeUser.item) || {};
  const isAdmin = activeUser.isAdmin || activeUser.isSuperAdmin || activeUser.isPowerAdmin;
  useEffect(() => dispatch.users.init(), []);

  return (
    <div className="deliveries">
      <PageWrap page="deliveries" t={t}>
        {!isAdmin && (
          <div className="navigation-links">
            <ul role="navigation">
              <li>
                <Link
                  to="/deliveries"
                  role="link"
                  style={{ color: "#4700a3" }}
                  index={0}
                  className={window.location.href.indexOf("all") <= -1 ? "selected" : ""}
                >
                  {t("deliveries_page_campaigns")}
                </Link>
              </li>
              <li>
                <Link
                  data-test="all_assets_link"
                  to="/deliveries/all"
                  role="link"
                  style={{ color: "#4700a3" }}
                  index={1}
                  className={window.location.href.indexOf("all") > -1 ? "selected" : ""}
                >
                  {t("deliveries_page_all_assets")}
                </Link>
              </li>
            </ul>
          </div>
        )}
        <TransitionBetween
          enter="fly left"
          exit="fly right"
          reverse={props.location.pathname === "/deliveries"}
          duration={500}
        >
          <div key={props.location.key}>
            <Switch location={props.location}>
              <Route
                exact
                path={match.path + "/"}
                render={(routeProps) =>
                  !isAdmin ? (
                    <Root t={t} dispatch={dispatch} {...routeProps} />
                  ) : (
                    <AssetTable t={t} dispatch={dispatch} isAdmin={isAdmin} />
                  )
                }
              />
              <Route
                path={match.path + "/all"}
                render={(routeProps) => (
                  <ShowAll dispatch={dispatch} t={t} isAdmin={isAdmin} {...routeProps} />
                )}
              />
              <Route
                path={match.path + "/:campaignId"}
                render={(routeProps) => (
                  <ShowCampaign dispatch={dispatch} t={t} {...routeProps} isAdmin={isAdmin} />
                )}
              />
            </Switch>
          </div>
        </TransitionBetween>
      </PageWrap>
    </div>
  );
}

const Root = ({ t, dispatch }) => {
  useEffect(() => {
    dispatch.campaigns.loadAll();
    dispatch.assets.loadLazy();
    dispatch.users.init();
  }, []);
  const { groups, isLoading, error } = useSelector(getGroupedAssets);
  const { isLazyLoading: assetsIsLazyLoading } = useSelector(getAssets);
  const { allIsLazyLoading } = useSelector(getCampaigns);
  const isLazyLoading = assetsIsLazyLoading || allIsLazyLoading;
  const [campaignsData, setCampaignsData] = useState();
  useEffect(() => {
    setCampaignsData(groups);
  }, [groups]);
  return (
    <LoadResolver isLoading={isLoading} error={error}>
      <div className="page-container" style={{ marginTop: "-2rem" }}>
        {!campaignsData || !campaignsData.length ? (
          <Message>
            {t("deliveries_page_no_data")}
            <br />
            <Link to="/deliverNew">{t("deliveries_page_deliver_new_link")}</Link>
          </Message>
        ) : (
          <DeliveriesTable
            t={t}
            dispatch={dispatch}
            data={campaignsData}
            isLazyLoading={isLazyLoading}
          />
        )}
      </div>
    </LoadResolver>
  );
};

const ShowAll = ({ dispatch, t, isAdmin }) => {
  const { isLoading, error } = useSelector(getAssets);
  return (
    <LoadResolver isLoading={isLoading} error={error} hideWhenLoading>
      <div className="page-container large">
        <AssetTable dispatch={dispatch} t={t} isAdmin={isAdmin} />
      </div>
    </LoadResolver>
  );
};

const ShowCampaign = (props) => {
  const { campaignId } = props.match.params;
  const { dispatch, t, isAdmin } = props;
  const campaignLookup = useSelector(getCampaignLookup);
  const campaign = campaignLookup[campaignId];
  const activeUser = useSelector((state) => state.activeUser.item) || {};

  useEffect(() => {
    if (isAdmin) {
      dispatch.agencys.load();
      dispatch.users.init();
    }
  }, []);

  const agencys = useSelector(getAgencys);
  const users = useSelector(getUsers);

  return (
    <div className="page-container">
      {!isAdmin && (
        <Breadcrumb
          className="bmo-purple-breadcrumb"
          icon="right angle"
          sections={[
            { key: "ini", content: t("pages_deliveries"), as: Link, to: "/deliveries" },
            {
              key: "cam",
              content: (
                <>
                  {t("deliveries_page_campaign")} <i>{campaign ? campaign.name : campaignId}</i>
                </>
              ),
              active: true,
            },
          ]}
        />
      )}
      <CampaignDisplay
        campaignId={campaignId}
        agencys={agencys.items}
        users={users.items}
        dispatch={dispatch}
        activeUser={activeUser}
        isAdmin={isAdmin}
      />
    </div>
  );
};

export default DeliveriesPage;
